import React, { PureComponent, RefObject } from 'react';
import { Flex } from 'app/components/common/Flex';
import { Range, TimeScale } from 'app/common/graph/Scale';
import { TimePeriod } from 'app/model/CORE';
import TimeUtils from 'app/common/util/TimeUtils';
import ConditionalRender from 'app/common/components/ConditionalRender';
import Utils from 'app/common/util/Utils';
import TimePeriodUtils from 'app/common/util/TimePeriodUtils';
import { Button, Form, Space } from 'antd';
import { CloseCircleOutlined, FormOutlined } from '@ant-design/icons';
import FormItem from 'antd/lib/form/FormItem';

import InputTimePeriod from '../../../common/input/InputTimePeriod';
import './TimePeriodSelector.less';

type ClickStatus = undefined | 'MIN' | 'MAX';

type GetStringValue = (date: Date) => string;

interface TimePeriodSelectorProps {
    legend?: string;
    width: string;
    hasManualHandler?: boolean;
    value: TimePeriod; // Selected time period
    onChange: (tp: TimePeriod) => void; // Selected time period change callback
    onSelectedPeriodConfirmed?: (tp: TimePeriod) => void;
    timePeriodRange: TimePeriod; // Boundaries for the time period selection
    currentTime: Date;
    markIntervalMs: number;
    labelDisplayMode?: LabelDisplayMode;
    showCurrentTime?: boolean;
    currentTimeLabel?: string | GetStringValue;
    highlights?: Array<TimePeriod>;
    currentTimeLineColor?: string;
    hideDownLabels?: boolean;
}

export enum LabelDisplayMode {
    ABSOLUTE,
    RELATIVE_TO_NOW,
}

interface TimePeriodSelectorState {
    scale: TimeScale;
    isInManualEntryMode: boolean;
}

export class TimePeriodSelector extends PureComponent<TimePeriodSelectorProps, TimePeriodSelectorState> {
    clicked: ClickStatus = undefined;
    svgRef: React.RefObject<SVGSVGElement> = React.createRef();

    constructor(props) {
        super(props);
        this.state = {
            scale: TimeScale.newTimeScale(new Range(0, 100), this.props.timePeriodRange),
            isInManualEntryMode: false,
        };
    }

    //if the period range changes then the scale needs to be "recalculated"
    componentDidUpdate(
        prevProps: Readonly<TimePeriodSelectorProps>,
        prevState: Readonly<TimePeriodSelectorState>,
        snapshot?: any,
    ) {
        if (TimePeriodUtils.compareTo(prevProps.timePeriodRange, this.props.timePeriodRange) !== 0) {
            this.setState({
                scale: TimeScale.newTimeScale(new Range(0, 100), this.props.timePeriodRange),
            });
        }
    }

    renderMarks = () => {
        if (!this.state.scale) return undefined;

        let time: Date = this.state.scale.toDate(this.state.scale.getScale().target.from);
        const marks = [];
        while (TimeUtils.diffInMs(this.state.scale.toDate(this.state.scale.getScale().target.to), time) > 0) {
            const x = this.state.scale.toSource(time);
            const color = TimePeriodUtils.contains(this.props.value, time) ? '#D6B85B50' : '#3B3B3B';
            marks.push(
                <line
                    key={`mark-${marks.length}`}
                    x1={x + '%'}
                    y1="25%"
                    x2={x + '%'}
                    y2="80%"
                    stroke={color}
                    strokeWidth="0.1em"
                    shapeRendering="geometricPrecision"
                />,
            );
            time = TimeUtils.addMillis(time, this.props.markIntervalMs);
        }
        return marks;
    };
    renderCursor = (time: Date, rectangle: boolean, lineColor: string) => {
        if (!this.state.scale || !time) return undefined;

        const x = this.state.scale.toSource(time);
        return [
            <line
                key="cursor-line"
                x1={x + '%'}
                y1="0%"
                x2={x + '%'}
                y2="100%"
                stroke={lineColor}
                strokeWidth="0.1em"
                shapeRendering="geometricPrecision"
            />,
            rectangle ? (
                <rect
                    key="cursor-rect"
                    x={x.toFixed(5) + '%'}
                    y="0%"
                    width={'0.3em'}
                    height={'0.3em'}
                    fill={'#FFFF00'}
                    style={{ transform: 'translateX(-0.15em)' }}
                />
            ) : undefined,
        ];
    };
    renderHighlights = () => {
        if (!this.state.scale || !this.props.highlights) return undefined;

        const elements = [];
        let index = 0;
        for (const highlightTimePeriod of this.props.highlights) {
            const coordinateStart = this.state.scale.toSource(highlightTimePeriod.from);
            const coordinateEnd = this.state.scale.toSource(highlightTimePeriod.to);

            elements.push(
                <line
                    key={`tps-highlight-${index++}`}
                    y1={'90%'}
                    x1={coordinateStart + '%'}
                    y2={'90%'}
                    x2={coordinateEnd + '%'}
                    stroke="red"
                    strokeWidth="0.4em"
                    shapeRendering="geometricPrecision"
                />,
            );
        }
        return elements;
    };
    handleMouseDown = e => {
        // If the click is before the mean between current MAX and MIN,
        // consider that MIN will change with mouse move
        const rect = this.svgRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;

        const newTime = TimeUtils.withoutSeconds(this.state.scale.toTarget((x * 100) / rect.width)).getTime();

        const rangeFrom = this.props.value.from.getTime();
        const rangeTo = this.props.value.to.getTime();

        if (2 * newTime < rangeFrom + rangeTo) {
            this.clicked = 'MIN';
        } else {
            this.clicked = 'MAX';
        }

        this.handleMouseMove(e);
    };
    handleMouseUp = e => {
        this.clicked = undefined;
        if (this.props.onSelectedPeriodConfirmed) {
            this.props.onSelectedPeriodConfirmed(this.props.value);
        }
    };
    handleMouseMove = e => {
        if (!this.clicked || !this.state.scale) return undefined;

        const rect = this.svgRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        if (x > 0) {
            const newTime = TimeUtils.withoutSeconds(this.state.scale.toTarget((x * 100) / rect.width));

            if (this.clicked === 'MIN' && newTime !== this.props.value?.from) {
                this.props.onChange({ from: newTime, to: this.props.value?.to });
            } else if (this.clicked === 'MAX' && newTime !== this.props.value?.to) {
                this.props.onChange({ from: this.props.value?.from, to: newTime });
            }
        }
    };
    handleMouseOut = e => {
        this.clicked = undefined;
    };
    handleTimePeriodManuallySet = (value: TimePeriod) => {
        if (value.from && value.to) {
            if (this.props.onSelectedPeriodConfirmed) {
                this.props.onSelectedPeriodConfirmed(value);
            }
            this.setState({
                isInManualEntryMode: false,
            });
        }
    };

    renderLabels = () => {
        if (!this.state.scale || !this.props?.value || !this.props.currentTime) return undefined;
        const timePeriodRangeFromLabel = TimeUtils.formatHHMM(this.props.timePeriodRange.from);
        let timePeriodRangeToLabel = TimeUtils.formatHHMM(this.props.timePeriodRange.to);
        timePeriodRangeToLabel = timePeriodRangeToLabel === '00:00' ? '24:00' : timePeriodRangeToLabel;

        return (
            <Flex size="20%" className="time-period-selector__label-holder">
                <span className="time-period-selector__label-holder__range -start">{timePeriodRangeFromLabel}</span>
                <span className="time-period-selector__label-holder__range -end">{timePeriodRangeToLabel}</span>

                <span
                    style={{
                        position: 'absolute',
                        left: `${this.state.scale?.toSource(this.props?.value?.from) || 0}%`,
                        transform: 'translate(-50%, -50%)',
                        fontWeight: 'bold',
                        fontSize: '0.85em',
                        color: 'white',
                    }}>
                    {this.getRangeBoundaryLabel(this.props?.value?.from)}
                </span>
                <ConditionalRender condition={!this.props.hideDownLabels}>
                    <span
                        style={{
                            position: 'absolute',
                            left: `${this.state.scale?.toSource(this.props?.value?.from) || 0}%`,
                            transform: 'translate(-50%, 300%)',
                            fontWeight: 'bold',
                            fontSize: '0.85em',
                            color: 'grey',
                        }}>
                        {TimeUtils.formatHHMM(this.props?.value?.from)}
                    </span>
                </ConditionalRender>
                {this.props.showCurrentTime && (
                    <>
                        <span
                            style={{
                                position: 'absolute',
                                left: `${this.state.scale?.toSource(this.props?.currentTime) || 0}%`,
                                transform: 'translate(-50%, -50%)',
                                fontSize: '0.85em',
                                color: 'gray',
                            }}>
                            {this.renderCurrentTimeLabel()}
                        </span>
                        <ConditionalRender condition={!this.props.hideDownLabels}>
                            <span
                                style={{
                                    position: 'absolute',
                                    left: `${this.state.scale?.toSource(this.props?.currentTime) || 0}%`,
                                    transform: 'translate(-50%, 300%)',
                                    fontSize: '0.85em',
                                    color: 'gray',
                                }}>
                                {TimeUtils.formatHHMM(this.props?.currentTime)}
                            </span>
                        </ConditionalRender>
                    </>
                )}
                <span
                    style={{
                        position: 'absolute',
                        left: `${this.state.scale?.toSource(this.props?.value?.to) || 0}%`,
                        transform: 'translate(-50%, -50%)',
                        fontWeight: 'bold',
                        fontSize: '0.85em',
                        color: 'white',
                    }}>
                    {this.getRangeBoundaryLabel(this.props?.value?.to)}
                </span>
                <ConditionalRender condition={!this.props.hideDownLabels}>
                    <span
                        style={{
                            position: 'absolute',
                            left: `${this.state.scale?.toSource(this.props?.value?.to) || 0}%`,
                            transform: 'translate(-50%, 300%)',
                            fontWeight: 'bold',
                            fontSize: '0.85em',
                            color: 'grey',
                        }}>
                        {TimeUtils.formatHHMM(this.props?.value?.to)}
                    </span>
                </ConditionalRender>
            </Flex>
        );
    };

    renderCurrentTimeLabel() {
        if (Utils.isExists(this.props.currentTimeLabel)) {
            if (typeof this.props.currentTimeLabel === 'function')
                return this.props.currentTimeLabel(this.props.currentTime);
            else return this.props.currentTimeLabel;
        } else {
            return TimeUtils.formatHHMM(this.props.currentTime);
        }
    }

    render() {
        return (
            <>
                <ConditionalRender condition={Utils.isExists(this.props.legend)}>
                    <Flex direction="vertical" style={{ flex: '0 0 auto', margin: '0 1em' }}>
                        <Flex size="20%" />
                        <Flex>
                            <span style={{ fontWeight: 'bold', alignSelf: 'center' }}>{this.props.legend}</span>
                        </Flex>
                    </Flex>
                </ConditionalRender>
                <ConditionalRender condition={this.props.hasManualHandler && !this.state.isInManualEntryMode}>
                    <Flex>
                        <Button onClick={() => this.setState({ isInManualEntryMode: true })}>
                            <FormOutlined />
                        </Button>
                    </Flex>
                </ConditionalRender>
                <ConditionalRender condition={this.state.isInManualEntryMode}>
                    <Flex grow={false} style={{ height: '2.5em' }}>
                        <Form size={'small'} autoComplete="off">
                            <Space.Compact>
                                <FormItem>
                                    <InputTimePeriod
                                        onChange={this.handleTimePeriodManuallySet}
                                        autoFocus
                                        referenceDay={this.props.currentTime}
                                        name={'selectedPeriod'}
                                        placeHolder={'hh:mm'}
                                    />
                                </FormItem>
                                <FormItem>
                                    <Button onClick={() => this.setState({ isInManualEntryMode: false })}>
                                        <CloseCircleOutlined />
                                    </Button>
                                </FormItem>
                            </Space.Compact>
                        </Form>
                    </Flex>
                </ConditionalRender>
                <ConditionalRender condition={!this.state.isInManualEntryMode}>
                    <Flex direction="vertical" id="timeline">
                        {this.renderLabels()}

                        <Flex style={{ position: 'relative' }}>
                            <span
                                style={{ position: 'absolute', width: '100%', height: '100%' }}
                                onMouseDown={this.handleMouseDown}
                                onMouseMove={this.handleMouseMove}
                                onMouseUp={this.handleMouseUp}
                                onMouseOut={this.handleMouseOut}
                            />
                            <svg
                                ref={this.svgRef}
                                style={{
                                    alignSelf: 'center',
                                    height: '2.5em',
                                    width: this.props.width,
                                    shapeRendering: 'crispEdges',
                                }}>
                                {this.renderHighlights()}
                                {this.renderMarks()}
                                <line
                                    key="horizontal-mid-line"
                                    x1="0%"
                                    y1="52.5%"
                                    x2="100%"
                                    y2="52.5%"
                                    stroke="gray"
                                    strokeWidth="0.1em"
                                    shapeRendering="geometricPrecision"
                                />
                                {this.props.showCurrentTime &&
                                    this.renderCursor(
                                        this.props.currentTime,
                                        false,
                                        Utils.isExists(this.props.currentTimeLineColor)
                                            ? this.props.currentTimeLineColor
                                            : '#D6B85B', // yellow
                                    )}
                                {this.renderCursor(this.props.value.from, true, '#D6B85B')}
                                {this.renderCursor(this.props.value.to, true, '#D6B85B')}
                            </svg>
                        </Flex>
                    </Flex>
                </ConditionalRender>
            </>
        );
    }

    private getRangeBoundaryLabel(date: Date) {
        switch (this.props.labelDisplayMode) {
            case LabelDisplayMode.ABSOLUTE:
                return TimeUtils.formatHHMM(date);
            case LabelDisplayMode.RELATIVE_TO_NOW:
                return (
                    <span>
                        {Math.round(TimeUtils.diffInMs(date, this.props.currentTime) / TimeUtils.HR_TO_MS)}
                        <sup>H</sup>
                    </span>
                );
            default:
                return TimeUtils.formatHHMM(date);
        }
    }
}
